import { useState, useMemo } from 'react';
import type { WsStatus } from '../api/wsClient';

interface Quote { sym: string; px: string; pct: number; }
interface Props {
  marketStatus: 'OPEN' | 'CLOSED';
  marketTime: string;
  tickers: Quote[];
  wsStatus: WsStatus;
  mfaSatisfied: boolean;
  username?: string;
  onSignOut: () => void;
  activeNav: 'Dashboard' | 'Markets' | 'Portfolio' | 'Orders';
  onNav: (n: 'Dashboard' | 'Markets' | 'Portfolio' | 'Orders') => void;
  canGoBack?: boolean;
  onBack?: () => void;
  onHome?: () => void;
  onClose?: () => void;
  activeBroker?: string;
  onChangeBroker?: (id: string) => void;
  brokerOptions?: Array<{ id: string; label: string; glyph: string }>;
  onToggleSidebar?: () => void;
}

export function TopBar({
  marketStatus, marketTime, tickers, wsStatus, mfaSatisfied, username,
  onSignOut, activeNav, onNav, canGoBack, onBack, onHome, onClose,
  activeBroker, onChangeBroker, brokerOptions, onToggleSidebar,
}: Props) {
  return (
    <div className="topbar">
      <div className="tb-navctrl" role="group" aria-label="Navigation">
        <button type="button" className="tb-navbtn tb-mobile-sidebar-toggle" onClick={onToggleSidebar} aria-label="Toggle sidebar" title="Toggle sidebar">☰</button>
        <button type="button" className="tb-navbtn" onClick={onBack} disabled={!canGoBack} aria-label="Back" title="Back">&#x2039;</button>
        <button type="button" className="tb-navbtn" onClick={onHome} aria-label="Home" title="Home">&#x2302;</button>
        <button type="button" className="tb-navbtn tb-close" onClick={onClose} aria-label="Close" title="Close">&times;</button>
      </div>
      <div className="brand">
        <span className="logo">N</span>
        <span>NigaStock</span>
      </div>
      <div className="tickers" aria-label="Live tickers">
        {tickers.map((t) => (
          <div className="t" key={t.sym}>
            <span className="sym">{t.sym}</span>
            <span className="px">{t.px}</span>
            <span className={`pct ${t.pct >= 0 ? 'up' : 'down'}`}>
              <span className={`arrow ${t.pct >= 0 ? 'up' : 'down'}`} />
              {(t.pct >= 0 ? '+' : '') + t.pct.toFixed(2)}%
            </span>
          </div>
        ))}
      </div>
      <div className="right">
        <span className={`market ${marketStatus === 'OPEN' ? '' : 'closed'}`}>
          &bull; Market {marketStatus.charAt(0) + marketStatus.slice(1).toLowerCase()} {marketTime}
        </span>
        <nav className="nav" aria-label="Primary">
          {(['Dashboard','Markets','Portfolio','Orders'] as const).map((k) => (
            <button key={k} type="button" className={`tb-nav-link ${activeNav === k ? 'active' : ''}`} onClick={() => onNav(k)} title={`Go to ${k}`}>{k}</button>
          ))}
          <a
            href="fx-trader.html"
            className="tb-fx-link"
            title="Switch to the FX trader"
            style={{ marginLeft: '6px', paddingLeft: '10px', borderLeft: '1px solid rgba(255,255,255,0.2)' }}
          >FX Trader →</a>
        </nav>
        {brokerOptions && brokerOptions.length > 0 && (
          <select
            className="tb-broker"
            value={activeBroker ?? brokerOptions[0]!.id}
            onChange={(e) => onChangeBroker && onChangeBroker(e.target.value)}
            aria-label="Route via broker"
            title="Route via"
          >
            {brokerOptions.map((o) => (
              <option key={o.id} value={o.id}>{o.glyph} {o.label}</option>
            ))}
          </select>
        )}
        <span className={`pill ${wsStatus === 'open' ? 'ok' : 'bad'}`}>WS</span>
        <span className={`pill ${mfaSatisfied ? 'ok' : 'bad'}`}>MFA</span>
        <button className="signout" onClick={onSignOut} title={username}>Logout</button>
      </div>
    </div>
  );
}

